import { Colors, FIXED_CIRCLE_DIMENSIONS, FOCUS_RING, Icon, Text } from '@amelio/ui-library';
import { useEffect, useRef } from 'react';

import { SURFACE_BORDER } from './surfaces';

import { useGetUserById } from '@/api/directory';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import { getFullName } from '@/utils/user';

// The strip's own geometry, named here so the summary cannot drift from the window around it.
// Every value is a step of the scale spelt out: `gap-3`, `rounded-2xl`, `px-5`, `py-3`.
const STRIP = `flex items-center justify-between gap-[12px] rounded-[16px] ${SURFACE_BORDER} bg-base-25 px-[20px] py-[12px]`;

interface SelectedEmployeeSummaryProps {
    employeeId: string;
    /** Moves the focus to the change button once shown, so a keyboard user lands after the choice they
     * just made instead of on the body of the dialog. */
    focusOnMount: boolean;
    onChange: () => void;
}

/**
 * The employee picked for the meeting, folded into one line once chosen: the search gives way to the
 * name, and « Modifier » brings the search back.
 */
export const SelectedEmployeeSummary = ({
    employeeId,
    focusOnMount,
    onChange,
}: SelectedEmployeeSummaryProps) => {
    const { t } = useAmelioTranslations();
    const changeButtonRef = useRef<HTMLButtonElement>(null);
    const { data: employee, isLoading, isError } = useGetUserById({ userId: employeeId });
    const hasName = Boolean(employee) && !isError;

    useEffect(() => {
        if (focusOnMount) {
            changeButtonRef.current?.focus();
        }
    }, [focusOnMount]);

    return (
        <div
            className={STRIP}
            data-testid={'selected-employee-summary'}
        >
            <div className={'flex min-w-0 items-center gap-[12px]'}>
                <span
                    aria-hidden={'true'}
                    className={`flex ${FIXED_CIRCLE_DIMENSIONS.md} bg-primary-50 shrink-0 items-center justify-center rounded-full`}
                >
                    <Icon
                        name={'users-user-edit'}
                        size={16}
                        color={Colors.PRIMARY_900}
                        ariaHidden
                    />
                </span>

                <div className={'flex min-w-0 flex-wrap items-center gap-x-[4px]'}>
                    <Text
                        size={'small'}
                        weight={'medium'}
                        color={Colors.BASE_500}
                    >
                        {t('thematicMeetings.newMeeting.employeeSelected')}
                    </Text>

                    {isLoading && (
                        <Skeleton>
                            <Bone className={'h-4 w-30'} />
                        </Skeleton>
                    )}

                    {!isLoading && hasName && (
                        <Text
                            size={'small'}
                            weight={'bold'}
                            color={Colors.NAVY}
                        >
                            {getFullName(employee)}
                        </Text>
                    )}

                    {/* A failed read names no one: « Anonyme » would read as the person chosen. */}
                    {!isLoading && !hasName && (
                        <Text
                            size={'small'}
                            weight={'medium'}
                            color={Colors.BASE_500}
                        >
                            {t('thematicMeetings.newMeeting.employeeNameUnavailable')}
                        </Text>
                    )}
                </div>
            </div>

            <button
                ref={changeButtonRef}
                type={'button'}
                onClick={onChange}
                aria-label={t('thematicMeetings.newMeeting.changeEmployeeLabel')}
                className={`${FOCUS_RING} hover:bg-primary-50 flex shrink-0 cursor-pointer items-center gap-[4px] rounded-lg px-[8px] py-[4px]`}
                data-testid={'selected-employee-change'}
            >
                <Icon
                    name={'edit-edit-2'}
                    size={14}
                    color={Colors.PRIMARY_900}
                    ariaHidden
                />

                <Text
                    size={'small'}
                    weight={'semibold'}
                    color={Colors.PRIMARY_900}
                >
                    {t('thematicMeetings.newMeeting.changeEmployee')}
                </Text>
            </button>
        </div>
    );
};
